import { lazy, Suspense, useEffect, useId, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Keyboard, X } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { useShownRoles } from "../../auth/useShownRoles";
import { focusFirstPageSearchInput } from "../command/useGlobalShortcuts";
import { hubsFor, screensFor } from "../hub/hubs";
import { motionPersonOf } from "../motion";
import { SearchDialog } from "./GlobalSearch";
import { ctrlPPrints } from "./goToModel";
import { closeHeaderPanel, openSearch, toggleHeaderPanel, useHeaderPanel } from "./headerStore";
import { recentFromPath, recordRecent } from "./searchModel";
import { HEADER_SHORTCUTS, useHeaderShortcuts, type ChordTarget } from "./useHeaderShortcuts";

// The Go to palette is its own chunk: fetched on first use (or when the pointer reaches the Go to button).
const GoToPaletteHost = lazy(async () => ({ default: (await import("./GoToPalette")).GoToPaletteHost }));

// Release 1.5.0: the header's keyboard layer and its panels (search dialog, Go to, keyboard shortcuts). Replaces the
// 1.4.0 CommandLayer while Release150 is on; also remembers record pages the person opens, for "Recent".

const TARGETS: Record<ChordTarget | "scenarios", string> = {
  hub: "",
  "work-orders": "/operations/work-orders",
  gantt: "/planning/orders",
  kanban: "/shop-floor/kanban",
  "floor-command": "/floor-command",
  scenarios: "/planning/scenarios"
};

/** Where a jump goes for this person, or null when the screen is not theirs (nothing happens then). */
export function jumpTarget(target: ChordTarget | "scenarios", roles: readonly string[]): string | null {
  if (target === "hub") return hubsFor(roles)[0]?.to ?? null;
  const to = TARGETS[target];
  return screensFor(roles).some((s) => s.to === to) ? to : null;
}

export function HeaderShortcuts() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const roles = useShownRoles();
  const { principal } = useAuth();
  const person = motionPersonOf(principal);
  const panel = useHeaderPanel();
  const [goToUsed, setGoToUsed] = useState(false);
  useEffect(() => {
    if (panel === "goto") setGoToUsed(true);
  }, [panel]);

  useHeaderShortcuts({
    enabled: roles.length > 0,
    onSearch: openSearch,
    onGoTo: () => toggleHeaderPanel("goto"),
    onCheatSheet: () => toggleHeaderPanel("shortcuts"),
    onJump: (t) => {
      const to = jumpTarget(t, roles);
      if (to) navigate(to);
    },
    onPageFilter: () => {
      if (!focusFirstPageSearchInput()) openSearch();
    },
    ctrlPPrints: () => ctrlPPrints(window.location.pathname)
  });

  // A page change closes any header panel and remembers record pages for "Recent".
  useEffect(() => {
    closeHeaderPanel();
    const item = recentFromPath(pathname);
    if (item) recordRecent(person, item, true);
  }, [pathname, person]);

  return (
    <>
      <SearchDialog />
      {goToUsed && (
        <Suspense fallback={null}>
          <GoToPaletteHost />
        </Suspense>
      )}
      <ShortcutsCheatSheet />
    </>
  );
}

/** The "?" cheat sheet. */
export function ShortcutsCheatSheet() {
  const panel = useHeaderPanel();
  const titleId = useId();
  if (panel !== "shortcuts") return null;
  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-slate-900/45 p-4 print:hidden" onClick={closeHeaderPanel} data-testid="shortcuts-help-backdrop">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            closeHeaderPanel();
          }
        }}
        data-testid="global-shortcuts-dialog"
      >
        <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
          <h2 id={titleId} className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Keyboard aria-hidden="true" className="h-4 w-4 text-brand-700" />
            Keyboard shortcuts
          </h2>
          <button
            type="button"
            autoFocus
            onClick={closeHeaderPanel}
            aria-label="Close keyboard shortcuts"
            className="rounded p-1 text-slate-500 hover:bg-slate-100"
            data-testid="shortcuts-help-close"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
        <dl className="mt-3 grid grid-cols-[auto,1fr] items-center gap-x-4 gap-y-2 text-xs">
          {HEADER_SHORTCUTS.map((s) => (
            <div key={s.keys.join("+")} className="contents">
              <dt className="flex items-center gap-1">
                {s.keys.map((k, i) => (
                  <kbd key={`${k}-${i}`} className="inline-flex min-w-[1.6rem] justify-center rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-mono text-xs font-semibold text-slate-800">
                    {k}
                  </kbd>
                ))}
              </dt>
              <dd className="text-slate-700">{s.description}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-3 border-t border-slate-100 pt-2.5 text-[11px] text-slate-500">
          Letters never act while you type in a box or while a window is open. “g” then a letter: press them one after the other.
        </p>
      </div>
    </div>
  );
}
